import { useState } from "react";
import {
  accountService,
  calendarService,
  homeService,
  taskService,
} from "@miplatform/api";
import {
  CONTACT_TYPES,
  HTTP_STATUSES,
  ISSUE_IMAGE_TYPES,
} from "@miplatform/constants";
import { useFeatureFlags } from "@miplatform/feature-flags";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  externalReferenceId,
  getDocumentTypeByContentType,
  isEmpty,
} from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { sortContactsPrimary } from "@mibuildweb/utils";
import { useGlobalManagerApproval } from "../GlobalManagerApprovalProvider/GlobalManagerApprovalProvider";

export const useAssignmentCreationViewModel = ({
  homeId = "",
  selectedOrderTemplate = {},
  edit = {},
  issues = [],
  onSubmit = () => null,
}) => {
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [isCreatingReportedBy, setIsCreatingReportedBy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [referenceIds, setReferenceIds] = useState({});
  const [issueTemplates, setIssueTemplates] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [tenantHolidays, setTenantHolidays] = useState([]);

  const stateManager = useComponentState();
  const { uploadDocument } = useDocumentsViewModel();
  const { executeApiRequest } = useGlobalManagerApproval();
  const { featureFlags } = useFeatureFlags();

  const [accountChanges, setAccountChanges] = useState([]);
  const [homeData, setHomeData] = useState(null);
  const getHomeInfo = () => {
    if (!homeId) {
      return null;
    }

    return stateManager.run(async () => {
      const { status, data } = await homeService.homes.getById({ id: homeId });
      const reportedBy = edit.getValue("reportedBy");

      if (status === HTTP_STATUSES.OK) {
        setContacts(sortContactsPrimary(data.contacts));
        setAccountChanges(data.accountChanges || []);
        setHomeData(data);

        if (reportedBy) {
          edit.update({
            reportedBy: data.contacts.find((c) => c.id === reportedBy.id) || "",
          });
        }

        const { referenceId, communityReferenceId } = data;
        setReferenceIds({
          [CONTACT_TYPES.COMMUNITIES]: communityReferenceId,
          [CONTACT_TYPES.HOMES]: referenceId,
        });

        return data;
      }
      return null;
    });
  };

  const refreshReportedByCreation = async (contact) => {
    const homeData = await getHomeInfo();
    setIsCreatingReportedBy(false);
    edit.update({
      reportedBy: homeData.contacts.find((c) => c.id === contact.id) || "",
    });
  };

  const createOrder = async (data, { callback } = {}) =>
    stateManager.run(async () => {
      const options = await taskService.order.getCreateOptions({ data });

      const response = await executeApiRequest({
        options,
        noInitialErrorMessage: true,
        callback,
      });

      return response;
    });

  const saveDocuments = async ({
    documents,
    externalReferenceId,
    externalData,
  }) =>
    Promise.all(
      documents.map((file) =>
        uploadDocument({
          file,
          documentType: getDocumentTypeByContentType(file.type),
          externalReferenceId,
          externalData,
        })
      )
    );

  const getIssueTemplates = async () => {
    const { data, status } = await taskService.orderTemplates.getById({
      id: selectedOrderTemplate.id,
    });

    if (status === HTTP_STATUSES.OK) {
      setIssueTemplates(
        data.issueTemplates.map((x) => ({ label: x.name, value: x.id }))
      );
    }
  };

  const getPurchaseOrders = () => {
    stateManager.run(async () => {
      const { status, data } = await accountService.billingCommitments.getAll({
        filters: {
          homeId: { type: "guid", value: homeId },
        },
        params: {
          includeDraftInvoices: true,
        },
      });

      if (status === HTTP_STATUSES.OK) {
        setPurchaseOrders(
          data.filter(
            ({ orderTemplateId }) =>
              orderTemplateId === selectedOrderTemplate.id
          )
        );
      }
    });
  };

  const getTenantHolidays = async () => {
    const response = await calendarService.tenantHolidays.getAll();
    if (response.status === HTTP_STATUSES.OK) {
      setTenantHolidays(response.data || []);
    }
  };

  const updateHomeAccount = async (accountId) => {
    if (!homeData) return;

    const options = await homeService.homes.getUpdateOptions({
      id: homeId,
      data: {
        accountId,
        modifiedOn: homeData.modifiedOn,
      },
    });

    await executeApiRequest({
      options,
      callback: ({ status }) => {
        if (status === HTTP_STATUSES.OK) {
          getHomeInfo();
        }
      },
    });
  };

  const handleOrderCreated = async (response) => {
    if (response.status !== HTTP_STATUSES.CREATED) return;
    toastService.success({ text: "Order created successfully." });
    onSubmit();

    const fileToSave = [];

    if (!isEmpty(issues)) {
      issues.forEach((issue) => {
        fileToSave.push({
          externalReferenceId: externalReferenceId.getIssueRefId({
            issueId: issue.id,
          }),
          documents: issue.issueDocuments,
          externalData: JSON.stringify({
            imageType: featureFlags?.miDayEnabled
              ? ISSUE_IMAGE_TYPES.ASSESS
              : ISSUE_IMAGE_TYPES.ORIGINATING,
          }),
        });
      });
    }

    fileToSave.push({
      externalReferenceId: externalReferenceId.getHomeRefId({
        homeId: response.data?.home?.id ?? homeId,
      }),
      externalData: JSON.stringify({
        orderTemplateId: response.data?.orderTemplateId,
        orderTemplateName: response.data?.orderTemplateName,
        tradeId: response.data?.orderTemplateTradeId,
        tradeName: response.data?.orderTemplateTradeName,
      }),
      documents: edit.getValue("documents"),
    });

    await Promise.all(fileToSave.map((file) => saveDocuments(file)));
  };

  const createAssignment = () => {
    stateManager.run(async () => {
      await createOrder(
        {
          ...edit.getValue(),
          accountId: edit.getValue("accountId") || undefined,
          recipientId: contacts.find(({ isPrimary }) => isPrimary)?.id,
          requesterId: edit.getValue("reportedBy")?.id,
          issues,
          orderTemplateId: selectedOrderTemplate.id,
          homeId,
        },
        { callback: handleOrderCreated }
      );
    });
  };

  return {
    isCreatingContact,
    isCreatingReportedBy,
    contacts,
    referenceIds,
    isLoading: stateManager.isBusy(),
    issueTemplates,
    purchaseOrders,
    accountChanges,
    homeData,
    refreshReportedByCreation,
    setIsCreatingContact,
    setIsCreatingReportedBy,
    tenantHolidays,
    getPurchaseOrders,
    getIssueTemplates,
    getHomeInfo,
    getTenantHolidays,
    updateHomeAccount,
    createAssignment,
  };
};
